import { AspectRatio, Box, BoxProps, Card, Stack, Typography } from "@xcorejs/ui";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useLayout } from "xcore";
import { News, NewsValues } from "xcore/types";
import { resolveSrcSet } from "../../../utils";
import { Skeleton } from "@mui/joy";
import { useRouter } from "next/router";

export type NewsCardNewsValuesFields = "title" | "intro" | "thumbnailIntro" | "content" | "images" | "layout" | "thumbnail";
export type NewsCardNewsValues = Pick<NewsValues, NewsCardNewsValuesFields>;

type NewsCardProps = {
  content: Pick<News<NewsCardNewsValuesFields>, "values" | "publishedAt" | "subCategories">;
  variant: "default" | "large" | "fullBg" | "outline";
  couldBeLarge?: boolean;
} & Omit<BoxProps, "content">;

const NewsCard = forwardRef<HTMLDivElement, NewsCardProps>(
  //@ts-ignore
  ({ content: { values, publishedAt, subCategories, id }, couldBeLarge, ...props }, ref) => {
    const { image, stringify, formatDate, richtextToString, localize, core } = useLayout();
    const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
    const [showLoading, setShowLoading] = useState<boolean>(false); // Shows debounced loading if image is still being downloaded
    const timeUntilShowLoading = 400; // in ms
    // todo xcc after localized images migrated remove workaround

    const [imagesResolved, setImagesResolved] = useState(localize(values?.images));

    const { locale } = useRouter();

    useEffect(() => {
      const callKek = async () => {
        if (locale) {
          const chhh = await core.content.singleById<News>(id, {
            fields: ["title", "intro", "images", "layout", "thumbnail", "content", "thumbnailIntro", "products"],
            locales: [locale, "en"],
          });

          if (chhh?.values.images?.en?.length) {
            setImagesResolved(chhh.values.images.en);
          }
        }
      };

      locale !== "en" && id && callKek();
    }, [locale]);

    const imgRef = useRef<HTMLImageElement | null>(null);

    const layout = stringify(values?.layout);

    const isFullBackground = layout === "fullBackground" || layout === "fullBackgroundAndWide";

    const img = values?.thumbnail
      ? layout === "fullBackgroundAndWide"
        ? image(values?.thumbnail, { width: 600, height: 450 })
        : layout === "fullBackground"
          ? image(values?.thumbnail, { width: 300, height: 480 })
          : image(values?.thumbnail, { width: 300, height: 225 })
      : imagesResolved
        ? layout === "fullBackgroundAndWide"
          ? image(imagesResolved?.[0], { width: 600, height: 450 })
          : layout === "fullBackground"
            ? image(imagesResolved?.[0], { width: 300, height: 480 })
            : image(imagesResolved?.[0], { width: 300, height: 225 })
        : "";

    const imgRetina = values?.thumbnail
      ? layout === "fullBackgroundAndWide"
        ? image(values?.thumbnail, { width: 600, height: 450, enlargement: 2 })
        : layout === "fullBackground"
          ? image(values?.thumbnail, { width: 300, height: 480, enlargement: 2 })
          : image(values?.thumbnail, { width: 300, height: 225, enlargement: 2 })
      : imagesResolved
        ? layout === "fullBackgroundAndWide"
          ? image(imagesResolved?.[0], { width: 600, height: 450, enlargement: 2 })
          : layout === "fullBackground"
            ? image(imagesResolved?.[0], { width: 300, height: 480, enlargement: 2 })
            : image(imagesResolved?.[0], { width: 300, height: 225, enlargement: 2 })
        : "";

    const titleColor = img ? (isFullBackground ? "#ffffff" : "#1e1e1e") : "#ffffff";
    const textColor = img ? (isFullBackground ? "#ffffff" : "#5e6062") : "#ffffff";
    const innerPadding = layout === "fullBackgroundAndWide" ? { _: "2rem", sm: "2rem 2rem 7rem 2rem" } : "2rem";

    const publishedAtResolved = typeof publishedAt === "string" ? publishedAt : localize(publishedAt);
    const publishedAtDateFormatted = publishedAtResolved ? formatDate(publishedAtResolved) : null;

    const thumbnailIntro = localize(values.thumbnailIntro);
    const intro = localize(values.intro);

    // Check on mount if the image is already loaded from cache
    useEffect(() => {
      if (imgRef.current?.complete) {
        setIsImageLoading(false);
      }
    }, []);

    useEffect(() => {
      if (isImageLoading) {
        // Show loading with debounce
        const timeout = setTimeout(() => setShowLoading(true), timeUntilShowLoading);

        return () => clearTimeout(timeout);
      }

      setShowLoading(false); // Do not show loading if image is loaded
    }, [isImageLoading]);

    return (
      <Card
        ref={ref}
        {...props}
        variant="elevated"
        media={
          <AspectRatio
            ratio={layout === "fullBackgroundAndWide" ? 113 / 300 : 225 / 300}
            pb={layout === "fullBackgroundAndWide" ? { _: "75%", sm: couldBeLarge ? "39%" : "75%" } : "75%"}
          >
            {!isFullBackground && img && (
              <>
                {showLoading && <Skeleton style={{ position: "absolute" }} variant="rectangular" width="100%" height="100%" />}{" "}
                {/* Skeleton loading must be separately. If image would be the child of Skeleton, it would fetch the image twice */}
                <img
                  ref={imgRef}
                  src={img}
                  {...resolveSrcSet(img, imgRetina)}
                  alt={stringify(values.title)}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  onLoad={() => setIsImageLoading(false)}
                />
              </>
            )}
          </AspectRatio>
        }
        _media={{
          justifyContent: "center",
          marginTop: 0,
          marginX: 0,
          bg: img ? "#fff" : "text",
          flex: "0 0 auto",
        }}
        body={
          <Box minWidth="100%" flex="1 1 100%">
            {isFullBackground && img && imgRetina && (
              <Box width="100%" height="100%" position="absolute" top={0} left={0}>
                {showLoading && <Skeleton style={{ position: "absolute" }} variant="rectangular" width="100%" height="100%" />}{" "}
                {/* Skeleton loading must be separately. If image would be the child of Skeleton, it would fetch the image twice */}
                <img
                  ref={imgRef}
                  src={img}
                  {...resolveSrcSet(img, imgRetina)}
                  alt={stringify(values.title)}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  style={{ objectFit: "cover" }}
                  onLoad={() => setIsImageLoading(false)}
                />
                <Box
                  height="23.5rem"
                  bg="linear-gradient(180deg, rgba(30, 30, 30, 0) 0%, black 100%)"
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  zIndex={1000}
                />
              </Box>
            )}
            <Stack gap="1rem" direction="column" position="relative" zIndex={1000}>
              {publishedAtDateFormatted && (
                <Typography
                  color={isFullBackground ? "#fff" : "gray"}
                  fontSize="1.2rem"
                  lineHeight="1.6rem"
                  {...(isFullBackground && { textShadow: "1px 1px #000000" })}
                >
                  {publishedAtDateFormatted}
                </Typography>
              )}
              <Typography
                variant="h4"
                color={titleColor}
                fontSize="1.5rem"
                lineHeight="2rem"
                {...(isFullBackground && { textShadow: "1px 1px #000000" })}
              >
                {stringify(values.title)}
              </Typography>
              <Typography
                variant="p"
                fontSize="1.5rem"
                lineHeight="2rem"
                color={textColor}
                display="-webkit-box"
                WebkitLineClamp={5}
                WebkitBoxOrient="vertical"
                overflow="hidden"
                {...(isFullBackground && { textShadow: "1px 1px #000000" })}
              >
                {thumbnailIntro ?? intro ?? richtextToString(values.content)}
              </Typography>
            </Stack>
          </Box>
        }
        _body={{ padding: innerPadding, bg: img ? "#fff" : "text", height: "100%" }}
        tag={stringify(subCategories?.["news-categories"]?.[0]?.name)}
        _tag={{
          position: "absolute",
          margin: 0,
          right: 0,
          top: 0,
          _group: { _hover: { bg: "#1e1e1e", borderColor: "#1e1e1e" } },
          zIndex: 1,
        }}
        transition="box-shadow 300ms, border 300ms"
        boxShadow="0 3px 10px 0 rgba(30, 30, 30, 0.25);"
        _hover={{ boxShadow: "0 0.6rem 1.5rem 0 rgba(30, 30, 30, 0.5)" }}
        maxWidth="none"
        height="100%"
      />
    );
  }
);

export default NewsCard;

export const newsCardNewsValuesFields: NewsCardNewsValuesFields[] = [
  "images",
  "title",
  "intro",
  "content",
  "thumbnail",
  "thumbnailIntro",
  "layout",
];
